import { CouchVariant } from './couchLibrary';
import { GridDimensions } from './geometry';

/**
 * Signed overhang of the placed couch cross-section past the image's top
 * and bottom edges, in mm. Positive means the couch extends that far
 * beyond the image; zero or negative means that side is covered. Sides
 * are IMAGE sides (along image Y, the couch's depth direction), not
 * patient directions — the check is about FOV coverage, which lives in
 * the image frame regardless of patient position.
 */
export interface CouchDepthOverhangMm {
    readonly topMm: number;
    readonly bottomMm: number;
}

/** Signed left/right overhang — the lateral counterpart of
 *  {@link CouchDepthOverhangMm}, in the same sign convention. */
export interface CouchLateralOverhangMm {
    readonly leftMm: number;
    readonly rightMm: number;
}

/**
 * A variant's cross-section extent in library mm, per axis. Depends on the
 * variant alone — the vertex walk is grid-independent, because mapping a
 * library mm offset to a voxel index is monotone affine, so the extreme
 * VOXEL indices are the images of the extreme MM offsets. Computed once
 * per variant instead of once per overhang call, which the panel
 * re-evaluates on every image, model and variant change.
 */
const boundsCache = new WeakMap<
    CouchVariant,
    { minXMm: number; maxXMm: number; minYMm: number; maxYMm: number }
>();

function variantBoundsMm(variant: CouchVariant) {
    const cached = boundsCache.get(variant);
    if (cached) {
        return cached;
    }
    let minXMm = Infinity;
    let maxXMm = -Infinity;
    let minYMm = Infinity;
    let maxYMm = -Infinity;
    for (const component of variant.components) {
        for (const loop of component.loopsXyMm) {
            for (const [x, y] of loop) {
                minXMm = Math.min(minXMm, x);
                maxXMm = Math.max(maxXMm, x);
                minYMm = Math.min(minYMm, y);
                maxYMm = Math.max(maxYMm, y);
            }
        }
    }
    if (minXMm === Infinity) {
        // Reporting an empty couch as "fully covered" would silently pass a
        // broken library entry through a blocking clinical check.
        throw new Error(
            `variantBoundsMm: variant ${variant.id} has no loop vertices`
        );
    }
    const bounds = { minXMm, maxXMm, minYMm, maxYMm };
    boundsCache.set(variant, bounds);
    return bounds;
}

/**
 * Signed overhang per side for one axis, in mm.
 *
 * `origin` is the voxel index the library's 0 mm sits at, `spacing` the
 * in-plane spacing along the axis and `size` the image extent in voxels.
 * The image spans voxel indices [0, size] in the corner convention the
 * placement works in, so scaling the index excess by the spacing turns it
 * into physical mm. Shared by both public entry points so the two halves
 * of a blocking clinical check cannot drift apart in sign convention.
 */
function overhangMm(
    minMm: number,
    maxMm: number,
    origin: number,
    spacing: number,
    size: number
): { lowMm: number; highMm: number } {
    const minVoxel = origin + minMm / spacing;
    const maxVoxel = origin + maxMm / spacing;
    return { lowMm: -minVoxel * spacing, highMm: (maxVoxel - size) * spacing };
}

/**
 * Lateral (left/right) FOV coverage, which needs no couch-top depth: the
 * cross-section's X extent is fixed by the variant and centred on the
 * image by construction ({@link placeCouchModel}'s lateral placement), so
 * left/right overhang is knowable from the model and image width alone.
 * `CouchOperationContent` uses this the moment a model is chosen, before
 * any line exists. The depth (top/bottom) extent does depend on the line,
 * and lives in {@link couchDepthOverhangMm}.
 */
export function couchLateralOverhangMm(
    variant: CouchVariant,
    grid: GridDimensions
): CouchLateralOverhangMm {
    const { minXMm, maxXMm } = variantBoundsMm(variant);
    const { lowMm, highMm } = overhangMm(
        minXMm,
        maxXMm,
        grid.sizeX / 2,
        grid.resX,
        grid.sizeX
    );
    return { leftMm: lowMm, rightMm: highMm };
}

/**
 * Measures how far the couch cross-section, placed with its top at
 * `couchTopVoxelY`, extends past the image's top and bottom edges — the
 * same voxel-space construction {@link placeCouchModel} uses, converted
 * back to mm per side.
 *
 * A couch that extends past the image is only PARTIALLY modelled: the
 * geometry outside the image extent is not in the dose grid, so a TPS
 * silently computes through less couch than physically exists — an
 * under-attenuation the planner cannot see.
 *
 * The cross-section is constant along Z (the library's documented
 * extrusion), so Z coverage is not a question this answers — placement
 * covers every slice by construction.
 */
export function couchDepthOverhangMm(
    variant: CouchVariant,
    couchTopVoxelY: number,
    grid: GridDimensions
): CouchDepthOverhangMm {
    const { minYMm, maxYMm } = variantBoundsMm(variant);
    const { lowMm, highMm } = overhangMm(
        minYMm,
        maxYMm,
        couchTopVoxelY,
        grid.resY,
        grid.sizeY
    );
    return { topMm: lowMm, bottomMm: highMm };
}

/**
 * How far the placed couch escapes the image, per direction, in mm, with
 * every side clamped at 0 so a covered side reads as "no overhang" rather
 * than as a negative margin.
 */
export interface CouchCoverage {
    /** The larger of the two lateral overhangs. */
    readonly lateralMm: number;
    readonly aboveMm: number;
    readonly belowMm: number;
    /** Any direction escapes the image, so the couch is only PARTIALLY
     *  modelled: the geometry outside the image extent is not in the dose
     *  grid, and a TPS would silently compute through less couch than
     *  physically exists — an under-attenuation the planner cannot see. */
    readonly isBlocked: boolean;
}

/**
 * The couch's FOV coverage as one verdict.
 *
 * `couchTopVoxelY` is nullable because the two halves become knowable at
 * different times: the cross-section's width is fixed by the variant and
 * centred on the image, so lateral coverage is answerable from the model
 * alone, while depth has no answer until the couch top is placed. A null
 * depth therefore reports the lateral half and leaves above/below at 0 —
 * enough to refuse a couch the image can never fit, before the user
 * spends any effort placing a line for it.
 */
export function couchCoverage(
    variant: CouchVariant,
    couchTopVoxelY: number | null,
    grid: GridDimensions
): CouchCoverage {
    const lateral = couchLateralOverhangMm(variant, grid);
    const depth =
        couchTopVoxelY === null
            ? null
            : couchDepthOverhangMm(variant, couchTopVoxelY, grid);

    const lateralMm = Math.max(0, lateral.leftMm, lateral.rightMm);
    const aboveMm = Math.max(0, depth?.topMm ?? 0);
    const belowMm = Math.max(0, depth?.bottomMm ?? 0);
    return {
        lateralMm,
        aboveMm,
        belowMm,
        isBlocked: lateralMm > 0 || aboveMm > 0 || belowMm > 0,
    };
}
